import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GraduationCap, Loader2, ShieldCheck } from "lucide-react";

const HERO =
  "https://images.unsplash.com/photo-1784001727397-1038beb23bca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBzY2hvb2wlMjBjYW1wdXMlMjBidWlsZGluZ3xlbnwwfHx8fDE3ODY3NTU1NTd8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, registerAlumni } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "register") {
        await registerAlumni(name, username, password);
        navigate("/alumni");
      } else {
        await login(username, password);
        navigate("/");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const isReg = mode === "register";

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left / form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-background relative">
        <div className="w-full max-w-md animate-fade-up">
          <div className="flex items-center gap-3 mb-10">
            <div className="h-11 w-11 rounded-lg bg-[#002147] flex items-center justify-center">
              <GraduationCap className="h-6 w-6 text-white" strokeWidth={2.2} />
            </div>
            <div>
              <p className="font-display font-extrabold text-lg leading-none text-[#002147]">
                Sistem Informasi
              </p>
              <p className="text-xs text-slate-500 mt-1">Sekolah Indonesia Jeddah</p>
            </div>
          </div>

          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold mb-2">
            {isReg ? "Pendaftaran Alumni" : "Selamat Datang"}
          </p>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter text-[#0F172A] mb-8">
            {isReg ? "Daftar Alumni" : "Masuk Akun"}
          </h1>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            {isReg && (
              <div className="space-y-2">
                <Label htmlFor="name" className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500">
                  Nama Lengkap
                </Label>
                <Input
                  id="name"
                  data-testid="register-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Nama lengkap Anda"
                  className="h-12"
                  required
                />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="username" className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500">
                {isReg ? "Buat Username" : "Username / NIS"}
              </Label>
              <Input
                id="username"
                data-testid="login-username-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={isReg ? "cth: rara.alumni" : "Admin ID atau Nomor Induk Siswa"}
                className="h-12"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500">
                Password
              </Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={isReg ? "Minimal 6 karakter" : "••••••••"}
                className="h-12"
                required
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-4 py-3"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full h-12 bg-[#002147] hover:bg-[#003366] text-white font-semibold rounded-md transition-colors"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : isReg ? "Daftar & Masuk" : "Masuk"}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-slate-500">
            {isReg ? (
              <>Sudah punya akun?{" "}
                <button type="button" data-testid="switch-to-login" onClick={() => { setMode("login"); setError(""); }} className="font-bold text-[#002147] hover:underline">Masuk di sini</button>
              </>
            ) : (
              <>Alumni sekolah?{" "}
                <button type="button" data-testid="switch-to-register" onClick={() => { setMode("register"); setError(""); }} className="font-bold text-[#002147] hover:underline">Daftar untuk isi Student Tracker</button>
              </>
            )}
          </div>

          {!isReg && (
            <div className="mt-8 flex items-start gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-4">
              <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0 text-[#002147]" />
              <span>
                Siswa masuk menggunakan <b>NIS</b> sebagai username. Password awal sama dengan NIS Anda.
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Right / hero */}
      <div className="hidden lg:block relative">
        <img src={HERO} alt="Kampus sekolah" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#002147]/70" />
        <div className="absolute bottom-0 left-0 p-12 text-white max-w-lg">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-amber-400 mb-3">
            Ekosistem Sekolah Digital
          </p>
          <h2 className="font-display font-extrabold text-4xl tracking-tight leading-tight">
            Satu data siswa, terintegrasi & aman.
          </h2>
          <p className="mt-4 text-white/80 leading-relaxed">
            Kelola buku induk, catatan pembayaran SPP, dan integrasi antar aplikasi sekolah dalam
            satu platform.
          </p>
        </div>
      </div>
    </div>
  );
}
